import React from 'react';
import { TentoLogo } from 'shared-components';
import { voltBaseUrl } from '@utils/platformBasedInfo';
import { TAP_TARGET_24_ROW, TAP_TARGET_44_ROW } from '@utils/tapTarget';

const TENTO_SYMBOL_SRC = 'https://media.tento.co/photos/tento-symbol.svg';
const TENTO_INSTAGRAM_URL = 'https://www.instagram.com/tento_co/';
const TENTO_LINKEDIN_URL = 'https://www.linkedin.com/company/tento-co/';
const TENTO_MARKETING_URL = 'https://www.tento.com';

export type SignUpLandingHowItWorksStep = {
  number: string;
  title: string;
  body: string;
};

export type SignUpLandingShellProps = {
  heroImage: string;
  heroAlt: string;
  heroLabel: string;
  heroHeadline: string;
  heroParagraph: string;
  hiwImage: string;
  hiwAlt: string;
  hiwHeadline: string;
  hiwSteps: SignUpLandingHowItWorksStep[];
  hiwBodySize?: 'lg' | 'sm';
  bottomCtaHeadline: string;
  primaryCtaLabel: string;
  onPrimaryCta: () => void;
};

const PrimaryCtaButton: React.FC<{ label: string; onClick: () => void; className?: string }> = ({
  label,
  onClick,
  className = '',
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`bg-[#d5d973] border border-[#b9bc63] rounded-full h-[45px] px-[40px] py-[7.5px] font-condensed text-[14px] uppercase !text-[#0d0d0d] text-center leading-[1.2] shadow-[0px_0px_10px_0px_rgba(13,13,13,0.1)] hover:bg-[#c5cd63] active:scale-95 transition-all ${className}`}
  >
    {label}
  </button>
);

const HowItWorksStep: React.FC<{
  step: SignUpLandingHowItWorksStep;
  isLast?: boolean;
  bodySize: 'lg' | 'sm';
}> = ({ step, isLast = false, bodySize }) => {
  // Body copy reduced by 2px across breakpoints per Apply QA.
  const bodyClasses =
    bodySize === 'lg'
      ? 'font-standard text-[14px] w640:text-[17px] w1024:text-[20.5px] leading-[1.1] text-[#0d0d0d] text-left'
      : 'font-standard text-[13px] w640:text-[14px] leading-[1.2] text-[#0d0d0d] text-left';

  return (
    <div
      className={`${isLast ? '' : 'border-b border-[#d5d973]'} py-[20px] flex flex-col gap-[20px] items-start w-full`}
    >
      <div className="flex h-[29px] items-start">
        <div className="bg-[#fff6f0] border border-[#d5cec8] rounded-full px-[20px] py-[5px] inline-flex items-center justify-center min-w-[58px]">
          <span className="font-extended text-[16px] text-[#0d0d0d] leading-[1.2]">{step.number}</span>
        </div>
      </div>
      <div className="flex flex-col gap-[10px] w-full">
        <h3 className="font-standard text-[22px] w640:text-[26px] w1024:text-[30px] leading-[1.1] text-[#0d0d0d] text-left">
          {step.title}
        </h3>
        <p className={bodyClasses}>{step.body}</p>
      </div>
    </div>
  );
};

type FooterListProps = { label: string; items: { label: string; href?: string }[] };

const FooterList: React.FC<FooterListProps> = ({ label, items }) => (
  <div className="flex-1 min-w-0 flex flex-col gap-[5px] items-start">
    <div className="pb-[5px]">
      <span className="font-condensed text-[14px] uppercase text-[#9e9e9e] leading-[1.2]">{label}</span>
    </div>
    {items.map((item) => (
      <a
        key={item.label}
        href={item.href ?? '#'}
        className={`font-standard text-[14px] !text-[#0d0d0d] leading-[1.2] !no-underline hover:!underline text-left ${TAP_TARGET_24_ROW}`}
      >
        {item.label}
      </a>
    ))}
  </div>
);

export const SignUpLandingShell: React.FC<SignUpLandingShellProps> = ({
  heroImage,
  heroAlt,
  heroLabel,
  heroHeadline,
  heroParagraph,
  hiwImage,
  hiwAlt,
  hiwHeadline,
  hiwSteps,
  hiwBodySize = 'lg',
  bottomCtaHeadline,
  primaryCtaLabel,
  onPrimaryCta,
}) => {
  return (
    <div className="bg-[#f2eae4] min-h-screen p-[10px] flex flex-col gap-[10px]">
      {/* Navigation Bar */}
      <header className="bg-[#fff6f0] border border-[#d5cec8] rounded-[5px] shadow-[0px_2px_5px_0px_rgba(13,13,13,0.05)] h-[57px] px-[20px] flex items-center gap-[10px] shrink-0">
        <div className="flex-1 flex items-center gap-[20px] min-w-0">
          <a
            href={TENTO_MARKETING_URL}
            aria-label="Tento Home"
            className="flex items-center !no-underline shrink-0"
            style={{ width: 109, height: 30 }}
          >
            <TentoLogo width="109" height="30" />
          </a>
        </div>
        <div className="flex-1 flex items-center justify-end gap-[10px] min-w-0">
          <a
            href={voltBaseUrl}
            className={`bg-[#d5d973] border border-[#b9bc63] rounded-full px-[15px] py-[7.5px] font-condensed text-[14px] uppercase !text-[#0d0d0d] text-center leading-[1.2] shadow-[0px_0px_10px_0px_rgba(13,13,13,0.1)] hover:bg-[#c5cd63] !no-underline hover:!no-underline transition-colors ${TAP_TARGET_44_ROW}`}
          >
            Sign in
          </a>
        </div>
      </header>

      {/* Main Landing Card */}
      <main
        className="relative border-b border-l border-r border-[#d5cec8] rounded-[15px] shadow-[0px_0px_10px_0px_rgba(13,13,13,0.05)] overflow-hidden p-[10px] w640:p-[20px] flex flex-col gap-[20px]"
        style={{
          background: 'linear-gradient(180deg, #fff6f0 0%, #fff6f0 35%, #e8e58e 80%, #d5d973 100%)',
        }}
      >
        {/* Hero / Cover Section */}
        <section className="flex flex-col w1024:flex-row gap-[20px] items-stretch justify-center w-full">
          {/* Cover - Image. Below the desktop breakpoint the photo is capped at
              45vh (F25, mobile UX audit 2026-10) so the label, headline and CTA
              start on the first screen instead of after a full-height photo;
              from w1024 the 726/825 aspect of the design applies. */}
          <div className="w-full h-[min(45vh,420px)] w1024:h-auto w1024:flex-1 w1024:max-w-[726px] aspect-[726/825] relative rounded-[10px] overflow-hidden">
            <img src={heroImage} alt={heroAlt} className="absolute inset-0 w-full h-full object-cover" />
          </div>

          {/* Cover - Information */}
          <div className="w-full w1024:flex-1 flex flex-col gap-[20px] items-start justify-center px-[10px] w640:px-[40px] w1024:px-[80px] py-[20px]">
            <div className="border-b border-[#d5d973] py-[5px] px-[5px] inline-flex items-center justify-center">
              <span className="font-condensed text-[14px] uppercase text-[#0d0d0d] leading-[1.2] text-center whitespace-nowrap">
                {heroLabel}
              </span>
            </div>
            <h1 className="font-extended text-[28px] w640:text-[36px] w1024:text-[42px] leading-[1.1] text-[#0d0d0d] w-full text-left m-0">
              {heroHeadline}
            </h1>
            <p className="font-standard text-[16px] leading-[1.2] text-[#0d0d0d] w-full text-left m-0">
              {heroParagraph}
            </p>
            <div className="pt-[20px] w640:pt-[40px] w-full flex items-end">
              <PrimaryCtaButton label={primaryCtaLabel} onClick={onPrimaryCta} />
            </div>
          </div>
        </section>

        {/* How It Works — extra top padding (pt-*) per Apply QA so the
            section breathes more above the headline. */}
        <section className="flex flex-col w1024:flex-row gap-[20px] items-stretch justify-center w-full pt-[40px] w640:pt-[60px] w1024:pt-[80px]">
          <div className="w-full w1024:flex-1 flex flex-col gap-[20px] items-start justify-center px-[10px] w640:px-[40px] w1024:px-[80px] py-[20px]">
            <div className="pb-[20px] w-full">
              <h2 className="font-extended text-[28px] w640:text-[36px] w1024:text-[42px] leading-[1.1] text-[#0d0d0d] text-left m-0">
                {hiwHeadline}
              </h2>
            </div>
            <div className="w-full flex flex-col gap-[10px] items-center justify-center">
              {hiwSteps.map((step, idx) => (
                <HowItWorksStep
                  key={step.number}
                  step={step}
                  isLast={idx === hiwSteps.length - 1}
                  bodySize={hiwBodySize}
                />
              ))}
            </div>
          </div>

          <div className="w-full w1024:flex-1 aspect-[726/825] relative rounded-[10px] overflow-hidden">
            <img src={hiwImage} alt={hiwAlt} className="absolute inset-0 w-full h-full object-cover" />
          </div>
        </section>

        {/* Get Started CTA */}
        <section className="bg-[#283310] rounded-[10px] flex flex-col items-center justify-center gap-[30px] w640:gap-[40px] px-[20px] w640:px-[40px] py-[60px] w640:py-[80px] w1024:py-[120px]">
          <div className="w-full max-w-[901.34px] flex flex-col items-center">
            <h2 className="font-extended text-[26px] w640:text-[34px] w1024:text-[42px] leading-[1.1] text-[#fff6f0] text-center w-full m-0">
              {bottomCtaHeadline}
            </h2>
          </div>
          <PrimaryCtaButton label={primaryCtaLabel} onClick={onPrimaryCta} />
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-[#f2eae4] border border-[#d5cec8] rounded-tl-[10px] rounded-tr-[10px] p-[20px] w640:p-[40px] flex flex-col w768:flex-row gap-[30px] w640:gap-[20px] items-start justify-center overflow-hidden shrink-0">
        {/* Footer - Left Side */}
        <div className="flex-1 min-w-0 flex flex-col gap-[10px] h-full items-start w-full">
          <a
            href={TENTO_MARKETING_URL}
            aria-label="Tento"
            className="block !no-underline"
            style={{ width: 290, maxWidth: '100%', height: 80 }}
          >
            <TentoLogo width="290" height="80" />
          </a>
          <span className="font-standard text-[14px] text-[#0d0d0d] leading-[1.2]">© Tento 2026</span>
        </div>

        {/* Footer - Center - Links */}
        <div className="flex-1 min-w-0 grid grid-cols-2 w640:grid-cols-3 gap-[10px] w-full">
          <FooterList
            label="PRODUCTS"
            items={[
              { label: 'Business Credit + Insights', href: `${voltBaseUrl}/business-credit/start` },
              { label: 'Equipment Financing', href: '/eq' },
              { label: 'Working Capital', href: '/' },
            ]}
          />
          <FooterList
            label="LEARN"
            items={[
              { label: 'Wellness Resources', href: TENTO_MARKETING_URL },
              { label: 'FAQs', href: `${TENTO_MARKETING_URL}/faqs` },
              { label: 'Our Mission', href: `${TENTO_MARKETING_URL}/mission` },
            ]}
          />
          <FooterList
            label="CONNECT"
            items={[
              { label: 'LinkedIn', href: TENTO_LINKEDIN_URL },
              { label: 'Instagram', href: TENTO_INSTAGRAM_URL },
            ]}
          />
        </div>

        {/* Footer - Right Side */}
        <div className="flex flex-col h-full items-start w768:items-end justify-between gap-[20px] shrink-0">
          <div className="size-[40px]">
            <img src={TENTO_SYMBOL_SRC} alt="Tento symbol" width={40} height={40} className="block w-full h-full" />
          </div>
          <div className="flex flex-col gap-[2.5px] items-start w768:items-end justify-end">
            <a
              href={`${TENTO_MARKETING_URL}/terms`}
              className={`font-standard text-[14px] !text-[#9e9e9e] leading-[1.2] !no-underline hover:!underline ${TAP_TARGET_24_ROW}`}
            >
              Terms &amp; Conditions
            </a>
            <a
              href={`${TENTO_MARKETING_URL}/privacy`}
              className={`font-standard text-[14px] !text-[#9e9e9e] leading-[1.2] !no-underline hover:!underline ${TAP_TARGET_24_ROW}`}
            >
              Privacy Policy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SignUpLandingShell;
